// src/pages/MyProducts.js
import React, { useState, useMemo, useEffect, useRef } from "react";
import useAuth from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import useDebounced from "../hooks/useDebounced";
import { useNavigate } from "react-router-dom";
import { db } from "../lib/firebase";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useToast } from "../contexts/ToastContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import {
  Search,
  Filter,
  Plus,
  Grid3X3,
  List,
  Package,
  Eye,
  EyeOff,
  Clock,
  X,
  Edit,
  Trash2,
  MoreVertical,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { errorDev } from "../utils/logger";
import { extractExpiryDate } from "../utils/formatExpiration";

function formatCountdown(target) {
  if (!target) return null;
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const mins = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours > 0) return `${hours}h ${remMins}m left`;
  return `${remMins}m left`;
}

function formatPrice(price, currency = "RWF") {
  if (price === 0 || price === "0") return "Free";
  if (price == null || price === "") return "";
  const n = Number(price);
  if (Number.isFinite(n)) {
    try {
      return (
        new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n) +
        ` ${currency}`
      );
    } catch (err) {
      errorDev("Operation failed:", err);
      return `${n} ${currency}`;
    }
  }
  return `${price} ${currency}`;
}

function formatLocation(loc) {
  if (!loc || typeof loc !== "object") return "";
  const parts = [loc.cell, loc.sector, loc.district].filter(Boolean);
  return parts.join(" · ");
}

function isProductHidden(product) {
  return Boolean(product?.hidden || product?.isHidden || product?.isPrivate);
}

const ProductCard = ({ product, onEdit, onDelete, onToggleHide, viewMode = "grid" }) => {
  const [showActions, setShowActions] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const actionsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setShowActions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const expiry = extractExpiryDate(product);
  const countdown = expiry ? formatCountdown(expiry) : null;
  const isExpired = expiry && expiry < new Date();
  const hidden = isProductHidden(product);
  const img = product.images?.[0] || product.image || "/images/placeholder.jpg";

  const handleAction = async (action) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    setShowActions(false);
    
    try {
      await action();
    } catch (error) {
      errorDev("Action failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  const baseCardClass = "bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer";
  const actionButtonClass = "flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50";

  if (viewMode === "list") {
    return (
      <div 
        className={`${baseCardClass} p-3`}
        onClick={handleCardClick}
      >
        <div className="flex gap-3">
          <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <img 
              src={img} 
              alt={product.title || product.name} 
              className="w-20 h-20 object-cover rounded-lg"
              loading="lazy"
              onError={(e) => (e.currentTarget.src = '/images/placeholder.jpg')}
            />
            <div className="absolute -top-1 -left-1 flex gap-1 flex-wrap">
              {hidden && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40">
                  Hidden
                </span>
              )}
              {product.sold && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-gray-900 text-white dark:bg-neutral-700">
                  Sold
                </span>
              )}
              {isExpired && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40">
                  Expired
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start mb-1">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                {product.title || product.name || 'Unnamed'}
              </h3>
              <div className="relative" ref={actionsRef} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowActions(!showActions)}
                  disabled={isUpdating}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  <MoreVertical size={16} />
                </button>
                {showActions && (
                  <div className="absolute right-0 top-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
                    <button
                      onClick={() => handleAction(() => onEdit(product))}
                      disabled={isUpdating}
                      className={`${actionButtonClass} text-gray-700 dark:text-gray-300`}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleAction(() => onToggleHide(product.id, !hidden))}
                      disabled={isUpdating}
                      className={`${actionButtonClass} text-blue-600 dark:text-blue-400`}
                    >
                      {hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                      {hidden ? 'Unhide' : 'Hide'}
                    </button>
                    <button
                      onClick={() => handleAction(() => onDelete(product.id))}
                      disabled={isUpdating}
                      className={`${actionButtonClass} text-red-600 dark:text-red-400 border-t border-gray-100 dark:border-gray-700`}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="text-green-700 dark:text-green-400 font-bold text-sm mb-1">
              {formatPrice(product.price, product.currency)}
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
              {product.category && (
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {product.category}
                </span>
              )}
              {product.unit && (
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {product.unit}
                </span>
              )}
            </div>

            <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
              <span>{formatLocation(product.location)}</span>
              {countdown && (
                <span className={`px-1.5 py-0.5 rounded ${
                  isExpired 
                    ? "bg-red-600 text-white" 
                    : "bg-black/60 text-white"
                }`}>
                  <Clock size={10} className="inline mr-1" />
                  {isExpired ? 'Expired' : countdown}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={baseCardClass}
      onClick={handleCardClick}
    >
      <div className="relative aspect-square bg-gray-100 dark:bg-neutral-800">
        <img
          src={img}
          alt={product.title || product.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => (e.currentTarget.src = '/images/placeholder.jpg')}
        />
        
        <div className="absolute left-2 top-2 flex gap-1 flex-wrap">
          {hidden && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40">
              Hidden
            </span>
          )}
          {product.sold && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-gray-900 text-white dark:bg-neutral-700">
              Sold
            </span>
          )}
          {isExpired && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40">
              Expired
            </span>
          )}
        </div>

        {countdown && (
          <div className={`absolute left-2 bottom-2 text-[10px] px-1.5 py-0.5 rounded ${
            isExpired 
              ? "bg-red-600 text-white" 
              : "bg-black/60 text-white"
          }`}>
            <Clock size={10} className="inline mr-1" />
            {isExpired ? 'Expired' : countdown}
          </div>
        )}

        <div className="absolute bottom-2 right-2" ref={actionsRef} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowActions(!showActions)}
            disabled={isUpdating}
            className="p-1.5 bg-white/90 dark:bg-neutral-800/80 rounded-full shadow-sm hover:shadow-md transition-all disabled:opacity-50"
          >
            <MoreVertical size={14} />
          </button>
          {showActions && (
            <div className="absolute right-0 bottom-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
              <button
                onClick={() => handleAction(() => onEdit(product))}
                disabled={isUpdating}
                className={`${actionButtonClass} text-gray-700 dark:text-gray-300`}
              >
                <Edit size={14} />
                Edit
              </button>
              <button
                onClick={() => handleAction(() => onToggleHide(product.id, !hidden))}
                disabled={isUpdating}
                className={`${actionButtonClass} text-blue-600 dark:text-blue-400`}
              >
                {hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                {hidden ? 'Unhide' : 'Hide'}
              </button>
              <button
                onClick={() => handleAction(() => onDelete(product.id))}
                disabled={isUpdating}
                className={`${actionButtonClass} text-red-600 dark:text-red-400 border-t border-gray-100 dark:border-gray-700`}
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 line-clamp-2 mb-2 min-h-[2.5rem]">
          {product.title || product.name || 'Unnamed'}
        </h3>
        
        <div className="text-green-700 dark:text-green-400 font-bold text-sm mb-2">
          {formatPrice(product.price, product.currency)}
        </div>

        <div className="flex flex-wrap gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
          {product.category && (
            <span className="truncate">{product.category}</span>
          )}
          {product.unit && (
            <>
              <span>•</span>
              <span>{product.unit}</span>
            </>
          )}
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {formatLocation(product.location)}
        </div>
      </div>
    </div>
  );
};

export default function MyProducts() {
  const { user } = useAuth();
  const { products, loading } = useProfile(user?.uid);
  const navigate = useNavigate();
  const toast = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const debouncedSearch = useDebounced(searchTerm, 350);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = !debouncedSearch || 
        (product.title || product.name || "").toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (product.category || "").toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (product.desc || "").toLowerCase().includes(debouncedSearch.toLowerCase());
      
      const matchesCategory = !categoryFilter || 
        (product.category || "").toLowerCase() === categoryFilter.toLowerCase();
      
      const expiry = extractExpiryDate(product);
      const isExpired = expiry && expiry < new Date();
      
      const matchesStatus = 
        statusFilter === "all" || 
        (statusFilter === "active" && !isProductHidden(product) && !product.sold && !isExpired) ||
        (statusFilter === "hidden" && isProductHidden(product)) ||
        (statusFilter === "sold" && product.sold) ||
        (statusFilter === "expired" && isExpired);
      
      return matchesSearch && matchesCategory && matchesStatus;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return new Date(a.createdAt?.toDate?.() || a.createdAt || 0) - 
                 new Date(b.createdAt?.toDate?.() || b.createdAt || 0);
        case "priceHigh":
          return (b.price || 0) - (a.price || 0);
        case "priceLow":
          return (a.price || 0) - (b.price || 0);
        case "name":
          return (a.title || a.name || "").localeCompare(b.title || b.name || "");
        default:
          return new Date(b.createdAt?.toDate?.() || b.createdAt || 0) - 
                 new Date(a.createdAt?.toDate?.() || a.createdAt || 0);
      }
    });

    return filtered;
  }, [products, debouncedSearch, categoryFilter, statusFilter, sortBy]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
    return uniqueCategories.sort();
  }, [products]);

  const stats = useMemo(() => {
    const expiredCount = products.filter(p => {
      const expiry = extractExpiryDate(p);
      return expiry && expiry < new Date();
    }).length;

    return {
      total: products.length,
      active: products.filter(p => !isProductHidden(p) && !p.sold && !(extractExpiryDate(p) && extractExpiryDate(p) < new Date())).length,
      hidden: products.filter(p => isProductHidden(p)).length,
      expired: expiredCount,
    };
  }, [products]);

  const handleEdit = (product) => {
    navigate(`/edit-product/${product.id}`);
  };

  const handleToggleHide = async (productId, hide) => {
    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, {
        hidden: hide,
        isHidden: hide,
        isPrivate: hide,
        updatedAt: new Date()
      });
    } catch (error) {
      toast.error("Failed to update product. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    setPendingDeleteId(productId);
  };

  const executeDelete = async (productId) => {
    if (!productId) return;

    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      const productRef = doc(db, "products", productId);
      await deleteDoc(productRef);
      toast.success("Product deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete product. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading.products) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-col items-center justify-center h-[50vh] text-center">
          <div className="orrith-loader orrith-loader-md orrith-loader-primary mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading your products...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header - Clean and Simple */}
      <header className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          My Products
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
          Where your farming genius meets marketplace reality. Track, manage, and conquer your product portfolio like a pro.
        </p>
        
        {/* Stats Overview - Clean and Minimal */}
        <div className="flex flex-wrap justify-center gap-4 mt-6 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
            <Package className="w-4 h-4 text-green-600" />
            <span><strong className="text-gray-900 dark:text-white">{stats.total}</strong> total products</span>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
            <Eye className="w-4 h-4 text-blue-600" />
            <span><strong className="text-gray-900 dark:text-white">{stats.active}</strong> active</span>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
            <EyeOff className="w-4 h-4 text-yellow-600" />
            <span><strong className="text-gray-900 dark:text-white">{stats.hidden}</strong> hidden</span>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
            <Clock className="w-4 h-4 text-red-600" />
            <span><strong className="text-gray-900 dark:text-white">{stats.expired}</strong> expired</span>
          </div>
        </div>
      </header>

      {/* Mobile filter toggle */}
      <div className="sm:hidden flex items-center justify-between mb-4">
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
        >
          <Filter size={16} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        
        <div className="flex border border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2.5 ${viewMode === "grid" ? "bg-green-600 text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
          >
            <Grid3X3 size={16} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2.5 ${viewMode === "list" ? "bg-green-600 text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`${showFilters ? 'block' : 'hidden'} sm:flex flex-col sm:flex-row sm:items-center gap-3 mb-6 flex-wrap bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700`}>
        <div className="relative w-full sm:flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input 
            type="text"
            placeholder="Search your products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
          />
        </div>

        <input 
          type="text"
          placeholder="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          list="categorySuggestions"
          className="w-full sm:w-auto flex-1 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
        />
        <datalist id="categorySuggestions">
          {categories.map((cat, i) => (
            <option key={i} value={cat} />
          ))}
        </datalist>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto flex-1 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="hidden">Hidden</option>
          <option value="expired">Expired</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full sm:w-auto flex-1 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="priceHigh">Price: High to Low</option>
          <option value="priceLow">Price: Low to High</option>
          <option value="name">Name: A to Z</option>
        </select>

        <div className="hidden sm:flex border border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2.5 ${viewMode === "grid" ? "bg-green-600 text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
          >
            <Grid3X3 size={16} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2.5 ${viewMode === "list" ? "bg-green-600 text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
          >
            <List size={16} />
          </button>
        </div>

        <button 
          onClick={() => {
            setSearchTerm("");
            setCategoryFilter("");
            setStatusFilter("all");
            setSortBy("newest");
          }}
          className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
        >
          Clear All
        </button>
      </div>

      {/* Active filters display */}
      {(categoryFilter || statusFilter !== "all" || searchTerm) && (
        <div className="flex flex-wrap gap-2 mb-6">
          {searchTerm && (
            <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-3 py-1.5 rounded-full">
              Search: {searchTerm}
              <button onClick={() => setSearchTerm("")} className="ml-1.5 text-blue-600 hover:text-blue-800">
                <X size={14} />
              </button>
            </span>
          )}
          {categoryFilter && (
            <span className="inline-flex items-center bg-green-100 text-green-800 text-xs px-3 py-1.5 rounded-full">
              Category: {categoryFilter}
              <button onClick={() => setCategoryFilter("")} className="ml-1.5 text-green-600 hover:text-green-800">
                <X size={14} />
              </button>
            </span>
          )}
          {statusFilter !== "all" && (
            <span className="inline-flex items-center bg-purple-100 text-purple-800 text-xs px-3 py-1.5 rounded-full">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter("all")} className="ml-1.5 text-purple-600 hover:text-purple-800">
                <X size={14} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredProducts.length} of {products.length} products
        </div>
        {filteredProducts.length > 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500">
            {viewMode === 'grid' ? 'Grid View' : 'List View'}
          </div>
        )}
      </div>

      {/* Products Grid/List */}
      {filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[40vh] text-center px-4 bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
          <Package className="w-20 h-20 text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-200 mb-2">
            {products.length === 0 ? "Your Empire Awaits" : "No Products Match"}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">
            {products.length === 0 
              ? "Ready to conquer the marketplace? Launch your first product and start your agricultural empire today."
              : "Your search didn't match any products. Try adjusting your filters or search terms."
            }
          </p>
          {products.length === 0 && (
            <button
              onClick={() => navigate("/sellnow")}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Your Empire
            </button>
          )}
        </div>
      ) : (
        <section className={
          viewMode === "grid" 
            ? "grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4"
            : "space-y-3"
        }>
          {filteredProducts.map(product => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleHide={handleToggleHide}
              viewMode={viewMode}
            />
          ))}
        </section>
      )}

      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title="Delete this product?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          const id = pendingDeleteId;
          setPendingDeleteId(null);
          await executeDelete(id);
        }}
      />
    </main>
  );
}